/**
 * @file Sidebar.js
 * @description Menú lateral con estadísticas por marca, configuraciones y logística
 * @module components/layout/Sidebar
 * 
 * Funcionalidades principales:
 * - Vista expandida/colapsada con animación
 * - Estadísticas detalladas por marca (inventario, traspasos, facturados)
 * - Sistema de flip 3D entre tres vistas: Marcas (pivote), Configuraciones y Logística
 * - Click en marcas y estadísticas para filtrar vehículos
 * - Indicadores visuales de alertas (vehículos sin RFID)
 * - Acceso a gestión de usuarios, roles y sitios (admin)
 * - Panel de logística con herramientas especializadas
 * - Auto-actualización de datos cada 60 segundos
 */

import React, { useState, useEffect } from 'react';
import { Menu, AlertTriangle, Activity, Package, ShoppingCart, LogOut, Settings, Car, Users, MapPin, UserCheck, Loader2, Truck, Home, Briefcase, History, Radio, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMarcas } from '../../services/apiService';
import { logout as logoutService } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import { getBrandColor } from '../../utils/brandColors';

/**
 * Rutas de logos de marcas
 */
const marcasLogos = {
  'FORD': '/logos/ford.png',
  'LINCOLN': '/logos/lincoln.png',
  'GWM': '/logos/gwm.png',
  'MAZDA': '/logos/mazda.png',
  'JEEP': '/logos/jeep.svg',
  'RAM': '/logos/ram.svg',
  'PEUGEOT': '/logos/peugeot.svg',
  'FIAT': '/logos/fiat.svg',
  'KIA': '/logos/kia.svg',
  'TOYOTA': '/logos/toyota.svg',
  'MITSUBISHI': '/logos/mitsubishi.svg',
  'HYUNDAI': '/logos/hyundai.svg',
  'BYD': '/logos/byd.svg',
  'BMW': '/logos/bmw.svg',
  'MINI': '/logos/mini.svg',
  'LEXUS': '/logos/lexus.svg',
};

/**
 * Componente Sidebar - Menú lateral del dashboard
 * 
 * Implementa un sidebar expansible con tres vistas:
 * 1. Vista de Marcas: Estadísticas detalladas por marca con clickable stats (PIVOTE)
 * 2. Vista de Configuraciones: Acceso a gestión de usuarios, roles y sitios
 * 3. Vista de Logística: Herramientas y opciones de logística
 * 
 * El cambio entre vistas usa animación flip 3D (rotateY).
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onExpandChange - Callback cuando cambia el estado expandido
 * @param {Function} props.onMarcaClick - Callback al hacer click en una marca
 * @param {Function} props.onUserManagementClick - Callback para abrir gestión de usuarios
 * @param {Function} props.onPatiosManagementClick - Callback para abrir gestión de sitios
 * @param {Function} props.onRoleManagementClick - Callback para abrir gestión de roles
 * @param {Function} props.onSimulateLectureClick - Callback para abrir popup de simular lectura
 * @param {Function} props.onStatisticClick - Callback al hacer click en una estadística específica
 * @param {Function} props.onUnidadesNoMayoreadasClick - Callback para abrir popup de unidades no mayoreadas
 * @param {Function} props.onFacturadasNoEntregadasClick - Callback para abrir popup de facturadas no entregadas
 * @returns {JSX.Element} Sidebar con estadísticas y configuraciones
 */
const Sidebar = ({
  onExpandChange = () => { },
  onMarcaClick,
  onUserManagementClick,
  onPatiosManagementClick,
  onRoleManagementClick,
  onStatisticClick,
  onUnidadesNoMayoreadasClick,
  onFacturadasNoEntregadasClick,
  onVendorsManagementClick,
  onSimulateLectureClick,
  onHistoryClick,
  initialView = 'marcas',
  showHomeDashboard = false,
  isExpandedExternal,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentView, setCurrentView] = useState(initialView);
  const [marcaSearch, setMarcaSearch] = useState('');
  const { logout } = useAuth();
  // Hook para obtener datos de marcas desde la API (actualización cada 60s)
  const { marcasData, loading, error, recargar } = useMarcas(60000);
  const navigate = useNavigate();

  const filteredMarcas = marcasData.filter(m =>
    m.marca.toLowerCase().includes(marcaSearch.toLowerCase())
  );

  // Sync with external expanded state (e.g. mobile menu button)
  useEffect(() => {
    if (isExpandedExternal !== undefined && isExpandedExternal !== isExpanded) {
      setIsExpanded(isExpandedExternal);
    }
  }, [isExpandedExternal]);

  // Clear search when sidebar closes
  useEffect(() => {
    if (!isExpanded) setMarcaSearch('');
  }, [isExpanded]);

  // Lock body scroll when sidebar is expanded (prevents background scrolling on mobile)
  useEffect(() => {
    if (isExpanded) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isExpanded]);
  /**
   * Maneja el toggle del menú (expandir/colapsar)
   * Al colapsar, siempre vuelve a la vista de marcas
   */
  const handleMenuClick = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onExpandChange(newExpandedState);

    if (!newExpandedState) {
      setCurrentView(initialView);
    }
  };

  /**
   * Maneja el click en el overlay para cerrar el sidebar
   */
  const handleOverlayClick = () => {
    setIsExpanded(false);
    onExpandChange(false);
    setCurrentView(initialView);
  };

  /**
   * Cambia a una vista específica
   * @param {string} view - Vista a mostrar ('marcas', 'settings', 'logistica')
   */
  const switchView = (view) => {
    setCurrentView(view);
  };

  /**
   * Maneja el click en una marca
   * Cierra el sidebar y ejecuta filtro por marca
   * 
   * @param {string} marca - Nombre de la marca clickeada
   */
  const handleMarcaClick = (marca) => {
    setIsExpanded(false);
    onExpandChange(false);

    if (onMarcaClick) {
      onMarcaClick(marca);
    }
  };

  /**
   * Maneja el click en una estadística específica
   * Permite filtrar por marca + tipo de estadística (inventario, traspaso, facturados)
   * 
   * @param {Event} e - Evento del click
   * @param {string} marca - Nombre de la marca
   * @param {string} statisticType - Tipo de estadística ('inventario'|'traspaso'|'facturados')
   */
  const handleStatisticClick = (e, marca, statisticType) => {
    e.stopPropagation();

    setIsExpanded(false);
    onExpandChange(false);

    if (onStatisticClick) {
      onStatisticClick(marca, statisticType);
    }
  };

  /**
   * Maneja el cierre de sesión
   */
  const handleLogout = () => {
    logout();
    logoutService();
  };

  /**
   * Maneja la actualización manual de datos
   */
  const handleRefresh = () => {
    recargar();
  };

  /**
   * Calcula el grado de rotación según la vista actual
   */
  const getRotationDegree = () => {
    switch (currentView) {
      case 'settings':
        return -180; // Volteo a la izquierda
      case 'logistica':
        return 180;  // Volteo a la derecha
      default:
        return 0;    // Vista de marcas (pivote central)
    }
  };

  /**
   * Obtiene el título según la vista actual
   */
  const getCurrentTitle = () => {
    switch (currentView) {
      case 'settings':
        return 'Configuración';
      case 'logistica':
        return 'Logística';
      default:
        return 'Marcas';
    }
  };

  const SIDEBAR_TRANSITION_MS = 350; // debe coincidir con duration-300

  return (
    <>
      {/* Sidebar principal */}
      <div
        className={`fixed top-0 h-full bg-white shadow-xl z-50 transition-all duration-200 ease-in-out
          right-0 sm:right-auto sm:left-0
          ${isExpanded
            ? 'translate-x-0 w-72 sm:w-80'
            : 'translate-x-full sm:translate-x-0 w-16'
          }`}
      >
        {/* Header del sidebar */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleMenuClick}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              {isExpanded && (
                <div className="ml-3 flex items-center gap-2 overflow-hidden">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 whitespace-nowrap truncate max-w-[130px]">
                    {getCurrentTitle()}
                  </h3>
                  {/* Indicadores de estado */}
                  {currentView === 'marcas' && loading && (
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
                  )}
                  {currentView === 'marcas' && error && (
                    <button
                      onClick={handleRefresh}
                      className="text-red-500 hover:text-red-700 text-xs underline shrink-0"
                      title={`Error: ${error}. Click para reintentar`}
                    >
                      ⚠️ Reintentar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Botones de acción (solo cuando está expandido) */}
            {isExpanded && (
              <div className="flex items-center gap-1 shrink-0">
                {/* Botón volver a Marcas (solo si no estamos en marcas) */}
                {currentView !== 'marcas' && (
                  <button
                    onClick={() => switchView('marcas')}
                    className="p-1.5 text-gray-600 hover:text-blue-600 transition-all duration-200 hover:bg-blue-50 rounded group"
                    title="Volver a Marcas"
                  >
                    <Car className="w-5 h-5" />
                  </button>
                )}

                {/* Toggle a Logística (desde marcas o configuraciones) */}
                {(currentView === 'marcas' || currentView === 'settings') && (
                  <button
                    onClick={() => switchView('logistica')}
                    className="p-1.5 text-gray-600 hover:text-orange-600 transition-all duration-200 hover:bg-orange-50 rounded group"
                    title="Logística"
                  >
                    <Truck className="w-5 h-5" />
                  </button>
                )}

                {/* Toggle a Configuraciones (desde marcas o logistica) */}
                {(currentView === 'marcas' || currentView === 'logistica') && (
                  <button
                    onClick={() => switchView('settings')}
                    className="p-2 text-gray-600 hover:text-gray-800 transition-all duration-200 hover:bg-gray-100 rounded group"
                    title="Configuración"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                )}

                {/* Home → Dashboard (solo en logística y cuando lo pida el padre) */}
                {showHomeDashboard && currentView === 'logistica' && (
                  <button
                    onClick={() => {
                      setIsExpanded(false);
                      onExpandChange(false);
                      setCurrentView(initialView);
                      navigate('/dashboard');
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 transition-all duration-200 hover:bg-blue-50 rounded group"
                    title="Ir al Dashboard"
                  >
                    <Home className="w-5 h-5" />
                  </button>
                )}

                {/* Botón de logout */}
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-600 hover:text-red-600 transition-all duration-200 hover:bg-red-50 rounded group"
                  title="Cerrar sesión"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}

          </div>
        </div>

        {/* Contenedor con efecto flip 3D */}
        <div className={`${isExpanded ? 'h-full' : 'h-full'} relative overflow-hidden`} style={{
          perspective: '1000px',
          backgroundColor: '#ffffff'
        }}>
          {/* Contenedor flip con las tres caras */}
          <div
            className="relative w-full h-full"
            style={{
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
              transform: `rotateY(${getRotationDegree()}deg)`
            }}
          >
            {/* CARA FRONTAL - Vista de Marcas (PIVOTE) */}
            <div
              className={`absolute inset-0 w-full h-full ${isExpanded ? 'p-4 space-y-4' : 'p-2 space-y-3'} ${isExpanded ? 'overflow-y-auto' : 'overflow-hidden'} overflow-x-hidden pb-20 bg-white`}
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg)',
                pointerEvents: currentView === 'marcas' ? 'auto' : 'none',
                opacity: currentView === 'marcas' ? 1 : 0
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              {/* Buscador de marcas — solo en vista expandida */}
              {isExpanded && currentView === 'marcas' && (
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    aria-label="Buscar marca"
                    value={marcaSearch}
                    onChange={e => setMarcaSearch(e.target.value)}
                    placeholder="Buscar marca..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1d2e61] focus:border-[#1d2e61]"
                  />
                </div>
              )}
              {isExpanded && currentView === 'marcas' && marcaSearch && filteredMarcas.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">
                  No se encontró ninguna marca
                </p>
              )}
              {/* Estados de carga y error */}
              {loading && marcasData.length === 0 && isExpanded && (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-500" />
                  <p className="text-gray-600">Cargando marcas...</p>
                </div>
              )}

              {error && marcasData.length === 0 && isExpanded && (
                <div className="text-center py-8">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                  <p className="text-red-600 mb-2">Error al cargar datos</p>
                  <button
                    onClick={handleRefresh}
                    className="text-blue-500 hover:text-blue-700 underline"
                  >
                    Reintentar
                  </button>
                </div>
              )}

              {/* Lista de marcas */}
              {(isExpanded ? filteredMarcas : marcasData.slice(0, 5)).map((marca, index) => (
                <div
                  key={`${marca.marca}-${index}`}
                  className={`rounded-lg transition-all duration-300 cursor-pointer ${isExpanded
                    ? 'bg-gray-50 p-4 hover:bg-gray-100'
                    : 'p-2 hover:bg-gray-100 flex justify-center'
                    }`}
                  onClick={() => handleMarcaClick(marca.marca)}
                >
                  {/* VISTA COLAPSADA - Solo logos con tooltip */}
                  {!isExpanded && (
                    <div className="flex flex-col items-center group relative">
                      <div className="relative">
                        <img
                          src={marcasLogos[marca.marca] || '/logos/default.png'}
                          alt={`${marca.marca} logo`}
                          className="w-8 h-8 object-contain transition-transform group-hover:scale-110"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        {/* Fallback si no hay imagen */}
                        <div
                          className="w-8 h-8 rounded border-2 bg-white flex items-center justify-center text-xs font-bold hidden"
                          style={{ borderColor: getBrandColor(marca.marca), color: getBrandColor(marca.marca) }}
                        >
                          {marca.marca.charAt(0)}
                        </div>

                      </div>
                      {/* Tooltip al hover */}
                      <div className="absolute left-16 bg-gray-900 text-white px-3 py-2 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                        <div className="font-medium">{marca.marca}</div>
                        <div>Gran Total: {marca.granTotal}</div>
                        <div>Inventario: {marca.inventario}</div>
                        {marca.tieneAlertas && (
                          <div className="text-red-300 mt-1">
                            ⚠️ {marca.totalAlertas} faltantes RFID
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* VISTA EXPANDIDA - Cards completas con estadísticas */}
                  {isExpanded && (
                    <>
                      {/* Header de la tarjeta */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <img
                            src={marcasLogos[marca.marca] || '/logos/default.png'}
                            alt={`${marca.marca} logo`}
                            className="w-6 h-6 object-contain mr-3"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div
                            className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs font-bold text-white mr-3 hidden"
                            style={{ backgroundColor: getBrandColor(marca.marca) }}
                          >
                            {marca.marca.charAt(0)}
                          </div>
                          <h4 className="font-semibold text-gray-900 whitespace-nowrap">
                            {marca.marca}
                          </h4>
                        </div>
                        {/* Badge de alertas */}
                        {marca.tieneAlertas && (
                          <div className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {marca.faltantesRFID || 0}
                          </div>
                        )}
                      </div>

                      {/* Gran Total con tooltip explicativo */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-2xl font-bold text-blue-600">
                            {marca.granTotal || 0}
                          </p>
                          <div className="group relative">
                            <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center cursor-help">
                              <span className="text-blue-600 font-bold text-[10px]">?</span>
                            </div>
                            <div className="absolute top-5 left-0 bg-gray-900 text-white p-2 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                              <div className="font-semibold mb-1">Gran Total</div>
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-1">
                                  <Package className="w-3 h-3 text-blue-600" />
                                  <span>Inventario: {marca.inventario || 0}</span>
                                  <span className="text-gray-400">+</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Activity className="w-3 h-3 text-blue-600" />
                                  <span>C. por llegar: {marca.enTransito || 0}</span>
                                </div>
                                <div className="border-t border-gray-700 pt-1 mt-1 flex justify-between">
                                  <span className="font-semibold">=</span>
                                  <span className="font-semibold">{marca.granTotal || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Gran Total (Disponible para venta)
                        </p>
                      </div>

                      {/* Estadísticas clickeables */}
                      <div className="space-y-2 mb-4">
                        {/* Inventario */}
                        <div
                          className="flex justify-between items-center text-sm p-2 rounded-md hover:bg-blue-50 transition-colors cursor-pointer group"
                          onClick={(e) => handleStatisticClick(e, marca.marca, 'inventario')}
                          title="Click para filtrar vehículos de inventario"
                        >
                          <div className="flex items-center">
                            <Package className="w-4 h-4 mr-2 text-blue-600 group-hover:scale-110 transition-transform" />
                            <span className="text-gray-600 group-hover:text-blue-700">Inventario:</span>
                          </div>
                          <div className="ml-auto text-right">
                            <span className="font-medium text-blue-600 group-hover:text-blue-700">{marca.inventario || 0}</span>
                          </div>
                        </div>

                        {/* Traspaso sin recibir */}
                        <div
                          className="flex justify-between text-sm p-2 rounded-md hover:bg-blue-50 transition-colors cursor-pointer group"
                          onClick={(e) => handleStatisticClick(e, marca.marca, 'traspaso')}
                          title="Click para filtrar vehículos en traspaso sin recibir"
                        >
                          <div className="flex items-center">
                            <Activity className="w-4 h-4 mr-2 text-blue-600 group-hover:scale-110 transition-transform" />
                            <span className="text-gray-600 group-hover:text-blue-700">Unidades por arribar:</span>
                          </div>
                          <div className="ml-auto text-right">
                            <span className="font-medium text-blue-600 group-hover:text-blue-700">{marca.enTransito || 0}</span>
                          </div>
                        </div>

                        {/* Facturados no entregados */}
                        <div
                          className="flex justify-between items-center text-sm p-2 rounded-md hover:bg-pink-50 transition-colors cursor-pointer group"
                          onClick={(e) => handleStatisticClick(e, marca.marca, 'facturados')}
                          title="Click para filtrar unidades facturadas"
                        >
                          <div className="flex items-center">
                            <ShoppingCart className="w-4 h-4 mr-2 text-pink-600 group-hover:scale-110 transition-transform" />
                            <span className="text-gray-600 group-hover:text-pink-700">Unidades facturadas:</span>
                          </div>
                          <div className="ml-auto text-right">
                            <span className="font-medium text-pink-600 group-hover:text-pink-700">{marca.facturadosNoEntregados || 0}</span>
                          </div>
                        </div>
                      </div>

                      {/* Panel de alertas */}
                      {marca.tieneAlertas && (
                        <div className="mb-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-400">
                          <div className="flex items-center mb-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 mr-2" />
                            <span className="text-sm font-medium text-red-800">Alertas detectadas:</span>
                          </div>
                          <div className="space-y-1">
                            {marca.faltantesRFID > 0 && (
                              <p className="text-xs text-red-700">
                                • {marca.faltantesRFID} vehículos sin detectar por RFID
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Barra de progreso */}
                      <div className="mb-2">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Total Físico Escaneado por RFID</span>                          <span>{marca.porcentajeInventario || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{
                              width: `${marca.porcentajeInventario || 0}%`,
                              backgroundColor: getBrandColor(marca.marca)
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Información adicional */}
                      <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
                        <div className="flex justify-between mb-1">
                          <span>Total físico esperado:</span>
                          <div className="flex items-center gap-1">
                            <div className="group relative">
                              <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center cursor-help">
                                <span className="text-gray-600 font-bold text-[10px]">?</span>
                              </div>
                              <div className="absolute bottom-5 right-0 bg-gray-900 text-white p-2 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                                <div className="font-semibold mb-1">Total físico esperado</div>
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-1 justify-between">
                                    <div className="flex items-center gap-1">
                                      <Package className="w-3 h-3 text-blue-600" />
                                      <span>Inventario: {marca.inventario || 0}</span>
                                    </div>
                                    <span className="text-gray-400">+</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <ShoppingCart className="w-3 h-3 text-pink-600" />
                                    <span>Facturados: {marca.facturadosNoEntregados || 0}</span>
                                  </div>
                                  <div className="border-t border-gray-700 pt-1 mt-1 flex justify-between">
                                    <span className="font-semibold">=</span>
                                    <span className="font-semibold">{marca.totalFisicoEsperado || 0}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <span className="font-medium">{marca.totalFisicoEsperado || 0}</span>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span>Lectura RFID:</span>
                          <span className={marca.lecturaRFID < marca.totalFisicoEsperado ? 'text-red-600 font-medium' : 'text-green-600'}>
                            {marca.lecturaRFID || 0}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {!isExpanded && marcasData.length > 5 && (
                <div className="flex justify-center mt-1">
                  <button
                    onClick={handleMenuClick}
                    className="bg-[#1d2e61] hover:bg-[#002e5b] text-white text-xs font-semibold rounded-full px-2 py-1 transition-colors"
                    title={`Ver todas las marcas (${marcasData.length - 5} más)`}
                  >
                    +{marcasData.length - 5}
                  </button>
                </div>
              )}
            </div>

            {/* CARA IZQUIERDA - Vista de Configuraciones */}
            <div
              className={`absolute inset-0 w-full h-full ${isExpanded ? 'p-4 space-y-4' : 'p-2 space-y-3'} overflow-y-auto overflow-x-hidden pb-20 bg-white`}
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                pointerEvents: currentView === 'settings' ? 'auto' : 'none',
                opacity: currentView === 'settings' ? 1 : 0
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              {isExpanded && (
                <div className="space-y-4">
                  {/* Header informativo de Configuraciones */}
                  <div className="border-2 border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                    <div className="flex justify-center mb-3">
                      <Settings className="w-12 h-12 text-gray-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-lg">Panel de Configuraciones</h4>
                    <p className="text-sm text-gray-600">
                      Administra usuarios, roles y sitios del sistema
                    </p>
                  </div>

                  {/* Opciones de configuración */}
                  <div className="space-y-2">
                    {/* Gestión de Usuarios */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        if (onUserManagementClick) {
                          onUserManagementClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <Users className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Usuarios</span>
                    </button>

                    {/* Gestión de Roles */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        if (onRoleManagementClick) {
                          onRoleManagementClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <UserCheck className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Roles</span>
                    </button>

                    {/* Gestión de Sitios */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        if (onPatiosManagementClick) {
                          onPatiosManagementClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <MapPin className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Sitios</span>
                    </button>

                    {/* Gestión de Vendedores */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        if (onVendorsManagementClick) {
                          onVendorsManagementClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                        }
                      }}
                    >
                      <Briefcase className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Vendedores</span>
                    </button>

                    {/* Configs de trazabilidad - ACTIVO */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        // 1) Cerrar sidebar
                        setIsExpanded(false);
                        onExpandChange(false);
                        setCurrentView(initialView);

                        // 2) Esperar a que termine la animación del sidebar
                        setTimeout(() => {
                          navigate('/traceabilityConfig');
                        }, SIDEBAR_TRANSITION_MS);
                      }}
                    >
                      <Activity className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Trazabilidad</span>
                    </button>

                    {/* Simular Lectura */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-300"
                      onClick={() => {
                        if (onSimulateLectureClick) {
                          onSimulateLectureClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <Radio className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Lectura</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* CARA DERECHA - Vista de Logística */}
            <div
              className={`absolute inset-0 w-full h-full ${isExpanded ? 'p-4 space-y-4' : 'p-2 space-y-3'} overflow-y-auto overflow-x-hidden pb-20 bg-white`}
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(-180deg)',
                pointerEvents: (currentView === 'logistica' || currentView === 'settings') ? 'auto' : 'none',
                opacity: (currentView === 'logistica' || currentView === 'settings') ? 1 : 0
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              {!isExpanded && currentView === 'logistica' && (
                <div className="flex flex-col items-center mt-4 group relative cursor-pointer" onClick={() => handleMenuClick()}>
                  <div className="p-2 bg-orange-50 rounded-lg text-orange-600 shadow-sm transition-transform group-hover:scale-110">
                    <Truck className="w-6 h-6" />
                  </div>
                  {/* Tooltip on hover */}
                  <div className="absolute left-14 bg-gray-900 text-white px-3 py-2 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 top-0">
                    <div className="font-medium">Panel de Logística</div>
                  </div>
                </div>
              )}

              {!isExpanded && currentView === 'settings' && (
                <div className="flex flex-col items-center mt-4 group relative cursor-pointer" onClick={() => handleMenuClick()}>
                  <div className="p-2 bg-gray-50 rounded-lg text-gray-600 shadow-sm transition-transform group-hover:scale-110">
                    <Settings className="w-6 h-6" />
                  </div>
                  {/* Tooltip on hover */}
                  <div className="absolute left-14 bg-gray-900 text-white px-3 py-2 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 top-0">
                    <div className="font-medium">Configuraciones</div>
                  </div>
                </div>
              )}

              {isExpanded && currentView === 'logistica' && (
                <div className="space-y-4">
                  {/* Header informativo de Logística */}
                  <div className="border-2 border-orange-300 rounded-lg p-6 text-center bg-orange-50">
                    <div className="flex justify-center mb-3">
                      <Truck className="w-12 h-12 text-orange-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-lg">Panel de Logística</h4>
                    <p className="text-sm text-gray-600">
                      Herramientas y control de logística vehicular
                    </p>
                  </div>

                  {/* Opciones de logística */}
                  <div className="space-y-2">
                    {/* Unidades No Mayoreadas */}
                    {/*<button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-orange-50 rounded-lg transition-colors duration-200 border border-transparent hover:border-orange-200"
                      onClick={() => {
                        if (onUnidadesNoMayoreadasClick) {
                          onUnidadesNoMayoreadasClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <Package className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-gray-700">Unidades No Mayoreadas</span>
                    </button>*/}

                    {/* Gestión de Traspasos y Facturación */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-orange-50 rounded-lg transition-colors duration-200 border border-transparent hover:border-orange-200"
                      onClick={() => {
                        if (onFacturadasNoEntregadasClick) {
                          onFacturadasNoEntregadasClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                          setCurrentView('marcas');
                        }
                      }}
                    >
                      <ShoppingCart className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-gray-700">Gestión de Traspasos y Facturación</span>
                    </button>
                    {/* Configs de trazabilidad */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-orange-50 rounded-lg transition-colors duration-200 border border-transparent hover:border-orange-200"
                      onClick={() => {
                        // 1) Cerrar sidebar
                        setIsExpanded(false);
                        onExpandChange(false);
                        setCurrentView(initialView);

                        // 2) Esperar a que termine la animación del sidebar
                        setTimeout(() => {
                          navigate('/traceability');
                        }, SIDEBAR_TRANSITION_MS);
                      }}
                    >
                      <Activity className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-gray-700">Seguimiento en tiempo real</span>
                    </button>

                    {/* Histórico */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-orange-50 rounded-lg transition-colors duration-200 border border-transparent hover:border-orange-200"
                      onClick={() => {
                        if (onHistoryClick) {
                          onHistoryClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                        }
                      }}
                    >
                      <History className="w-5 h-5 text-orange-600" />
                      <span className="font-medium text-gray-700">Histórico</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Panel de Configuraciones (Settings) */}
              {isExpanded && currentView === 'settings' && (
                <div className="space-y-4">
                  <div className="border-2 border-gray-300 rounded-lg p-6 text-center bg-gray-50">
                    <div className="flex justify-center mb-3">
                      <Settings className="w-12 h-12 text-gray-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-lg">Panel de Configuraciones</h4>
                    <p className="text-sm text-gray-600">
                      Administra usuarios, roles y sitios del sistema
                    </p>
                  </div>

                  <div className="space-y-2">
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        if (onUserManagementClick) onUserManagementClick();
                        setIsExpanded(false);
                        onExpandChange(false);
                      }}
                    >
                      <Users className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Usuarios</span>
                    </button>

                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        if (onRoleManagementClick) onRoleManagementClick();
                        setIsExpanded(false);
                        onExpandChange(false);
                      }}
                    >
                      <UserCheck className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Roles</span>
                    </button>

                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        if (onPatiosManagementClick) onPatiosManagementClick();
                        setIsExpanded(false);
                        onExpandChange(false);
                      }}
                    >
                      <MapPin className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Sitios</span>
                    </button>

                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        if (onVendorsManagementClick) {
                          onVendorsManagementClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                        }
                      }}
                    >
                      <Briefcase className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Vendedores</span>
                    </button>

                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        setIsExpanded(false);
                        onExpandChange(false);
                        setTimeout(() => {
                          navigate('/traceabilityConfig');
                        }, SIDEBAR_TRANSITION_MS);
                      }}
                    >
                      <Activity className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Trazabilidad</span>
                    </button>

                    {/* Simular Lectura */}
                    <button
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-100 rounded-lg transition-colors duration-200 border border-transparent hover:border-gray-200"
                      onClick={() => {
                        if (onSimulateLectureClick) {
                          onSimulateLectureClick();
                          setIsExpanded(false);
                          onExpandChange(false);
                        }
                      }}
                    >
                      <Radio className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-700">Lectura</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overlay de fondo cuando está expandido */}

      {isExpanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-10 z-40 overscroll-none"
          onClick={handleOverlayClick}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}
    </>
  );
};

export default Sidebar;